import type { DatasetBase } from "@models/DatasetBase";

/**
 * A widget's desired base membership after a save/delete:
 * - baseId = the Dataset-Level Base it now references.
 * - baseId = null  → no pre-configured base (custom/all, or the widget is deleted):
 *   the widget is removed from every base.
 */
export interface WidgetBaseMembership {
  widgetId: string;
  dashboardId: string;
  baseId: string | null;
}

/**
 * Applies widget membership changes to a dataset's bases, keeping each widget in
 * at most one base's usedWidgets. Only *real* changes count: re-saving a widget
 * whose base membership is unchanged returns `changed: false` so the caller can
 * skip the dataset PATCH. Multiple updates are applied in one pass so a cascade
 * delete produces a single reconciled result.
 */
export function reconcileBaseUsage(
  bases: DatasetBase[],
  updates: WidgetBaseMembership[],
): { bases: DatasetBase[]; changed: boolean } {
  // Current membership: widgetId -> baseId it currently sits in.
  const currentBaseByWidget = new Map<string, string>();
  bases.forEach((b) =>
    (b.usedWidgets ?? []).forEach((w) =>
      currentBaseByWidget.set(w.widgetId, b.id),
    ),
  );

  // Keep only updates that actually move a widget to a different base (or none).
  const effective = updates.filter((u) => {
    const current = currentBaseByWidget.get(u.widgetId) ?? null;
    return current !== u.baseId;
  });

  if (effective.length === 0) return { bases, changed: false };

  const next = bases.map((b) => ({
    ...b,
    usedWidgets: [...(b.usedWidgets ?? [])],
  }));

  for (const update of effective) {
    // Remove from wherever it currently lives.
    for (const base of next) {
      const idx = base.usedWidgets.findIndex(
        (w) => w.widgetId === update.widgetId,
      );
      if (idx !== -1) base.usedWidgets.splice(idx, 1);
    }
    // Add to the target base (if any and it still exists).
    if (update.baseId) {
      const target = next.find((b) => b.id === update.baseId);
      if (target) {
        target.usedWidgets.push({
          widgetId: update.widgetId,
          dashboardId: update.dashboardId,
        });
      }
    }
  }

  return { bases: next, changed: true };
}

/** Total linked visualizations for a base — the "Linked visualizations" column value. */
export function getLinkedVisualizationCount(base: DatasetBase): number {
  return base.usedWidgets?.length ?? 0;
}

/** Distinct dashboards a base is used across — for the save-impact message. */
export function getLinkedDashboardCount(base: DatasetBase): number {
  return new Set((base.usedWidgets ?? []).map((w) => w.dashboardId)).size;
}
